import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { healthService, HealthResponse } from '@services/health-service';
import { setStatus } from '../app/appSlice';
import i18next from 'i18next';

interface HealthState {
  status: string | null;
  version: string | null;
  uptime: number | null;
  timestamp: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: HealthState = {
  status: null,
  version: null,
  uptime: null,
  timestamp: null,
  isLoading: false,
  error: null,
};

export const checkHealth = createAsyncThunk<HealthResponse, void, { rejectValue: string }>(
  'health/check',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await healthService.checkHealth();

      // Actualizar el estado de la aplicación con la información de salud
      dispatch(
        setStatus({
          type: 'success',
          message: i18next.t('status.ready'),
          details: i18next.t('status.details'),
        })
      );

      return response;
    } catch (error) {
      const errorMessage = i18next.t('status.systemsDown');

      // Extraer un mensaje de error útil
      let errorDetails = '';
      if (error instanceof Error) {
        errorDetails = error.message;
      } else if (typeof error === 'object' && error !== null) {
        // Intentar extraer información útil del objeto de error
        if ('message' in error) {
          errorDetails = String(error.message);
        } else if ('error' in error) {
          errorDetails = String(error.error);
        } else if ('status' in error) {
          errorDetails = `Error ${error.status}`;
        } else {
          // Si no hay propiedades útiles, usar un mensaje genérico
          errorDetails = i18next.t('status.error');
        }
      } else {
        // Si no es un objeto, convertir a string
        errorDetails = String(error);
      }

      // Actualizar el estado de la aplicación con el error
      dispatch(
        setStatus({
          type: 'error',
          message: errorMessage,
          details: errorDetails,
        })
      );

      return rejectWithValue(errorMessage);
    }
  }
);

const healthSlice = createSlice({
  name: 'health',
  initialState,
  reducers: {
    resetHealth: state => {
      11;
      state.status = null;
      state.version = null;
      state.uptime = null;
      state.timestamp = null;
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(checkHealth.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkHealth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = action.payload.status;
        state.version = action.payload.version;
        state.uptime = action.payload.uptime;
        state.timestamp = action.payload.timestamp;
        state.error = null;
      })
      .addCase(checkHealth.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || i18next.t('status.systemsDown');
      });
  },
});

export const { resetHealth } = healthSlice.actions;

export default healthSlice.reducer;
